<template>
  <v-file-input
    :model-value="modelValue"
    class="brand-file-drop rounded-lg"
    :class="{'is-dragging': dragging}"
    :accept="accept"
    :disabled="disabled"
    v-bind="$attrs" variant="outlined" prepend-icon=""
    :multiple="multiple" hide-details="auto"
    @drop.prevent="addDropFile"
    @dragover.prevent="dragging = true"
    @dragenter.prevent="dragging = true"
    @dragleave.prevent="dragging = false"
    @update:model-value="onFileInput"
    :clearable="false"
  >
    <template #prepend-inner>
      <slot>
        <div class="text-center mx-auto pa-6">
          <PhFileText class="text-teal400 text-h3 mb-4" weight="fill" />
          <h4 class="text-body-2 text-neutral900 font-weight-semibold" v-text="titleComputed" />
          <p v-if="subtitle" class="text-caption text-neutral400 mb-0" v-text="subtitle" />
        </div>
      </slot>
    </template>
  </v-file-input>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { PhFileText } from '@phosphor-icons/vue'

// Define component props
const props = defineProps({
  modelValue: {
    type: [File, Array],
    default: null,
  },
  accept: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
  subtitle: {
    type: String,
    default: null,
  },
  multiple: Boolean,
  disabled: {
    type: Boolean,
    default: false,
  },
  replace: {
    type: Boolean,
    default: false,
  },
})

// Define emits
const emit = defineEmits(['update:model-value'])

// Define component options
defineOptions({
  inheritAttrs: false,
})

// Setup composables
const { t } = useI18n()

// Define reactive state
const dragging = ref(false)

// Computed properties
const titleComputed = computed(() => {
  return props.title ?? t('component.filedropper.title')
})

// Methods
const validateFile = (file) => {
  if (!props.accept) return true

  return props.accept
    .replace(/\s/g, '').split(',')
    .some(accept => new RegExp(accept.replace('*', '.*')).test(file.type))
}

const addDropFile = (e) => {
  dragging.value = false

  let files = Array.from(e.dataTransfer.files)
  files = files.filter(i => validateFile(i))
  if (!files.length) return

  if (props.multiple) {
    if (props.replace && Array.isArray(props.modelValue)) {
      // Create a new array with existing items and new files
      const newValue = [...props.modelValue, ...files]
      emit('update:model-value', newValue)
    } else {
      emit('update:model-value', files)
    }
  } else {
    const [file] = files
    emit('update:model-value', file)
  }
}

const onFileInput = (files) => {
  emit('update:model-value', files)
}
</script>

<style lang="scss" scoped>
.brand-file-drop {
  &:deep(.v-field__input) {
    display: none !important;
  }

  &:deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral200));

      .v-field__outline__start {
        border-left-style: dashed;
        border-left-width: 2px;
        border-top-style: dashed;
        border-top-width: 2px;
        border-bottom-style: dashed;
        border-bottom-width: 2px;
      }

      .v-field__outline__notch {
        border-top-style: dashed;
        border-top-width: 2px;
      }

      .v-field__outline__end {
        border-right-style: dashed;
        border-right-width: 2px;
        border-top-style: dashed;
        border-top-width: 2px;
        border-bottom-style: dashed;
        border-bottom-width: 2px;
      }
    }
  }

  &.is-dragging :deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)),
  &:hover :deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)) {
    .v-field__outline {
      color: rgb(var(--v-theme-teal900));
    }
  }

  &:deep(.v-field) {
    display: grid;

    .v-field__prepend-inner {
      grid-column: 1 / -1;
      width: 100%;
      margin: 0;
      z-index: 1;
      pointer-events: none;
    }

    .v-field__field {
      @include overlay;

      cursor: pointer;
      grid-column: 1 / -1;

      .v-file-input__text {
        display: none;
      }
    }

    .v-field__append-inner {
      display: none;
    }
  }
}
</style>

<style lang="scss">
// Dark mode specific styles - unscoped to ensure proper cascade
.v-theme--dark {
  .brand-file-drop:deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral50)) !important;
    }
  }

  .brand-file-drop.is-dragging:deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)),
  .brand-file-drop:hover:deep(.v-field--variant-outlined:not(.v-field--disabled):not(.v-field--error):not(.v-field--focused)) {
    .v-field__outline {
      color: rgb(var(--v-theme-teal600)) !important;
    }
  }
}
</style>
